import { useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { fetchSpotlights } from '../utils/api';
import { spotlightQuery, toSearchString } from '../utils/urlState';
import type { Spotlight } from '../types';
import { useT } from '../i18n';

export type { Spotlight };

const Section = styled.div`
  display: flex;
  flex-direction: column;
  /* No gap: the rail's shadow padding (see ScrollRail) already sits between the
     title and the cards, so a gap here would stack on top of it. The rail's
     padding-top carries the full title→cards distance instead. */
  margin-bottom: ${theme.spacing.lg};
`;

// Section caption — matches the budget widget's "Spotlights" label.
// h2: a top-level region of the widget, under the host page's h1. `&&` doubles
// the class so a host theme's `h2` rule can't take over the type scale — see
// CardTitle in styles/components.tsx for the full reasoning.
const SectionTitle = styled.h2`
  && {
    /* dz-shared-styles sets text-align: center on bare h2, and the host theme
       adds horizontal padding. The && wrapper only beats them for properties
       declared here, so both have to be declared. */
    text-align: left;
    padding: 0;
    font-family: var(--sans-stack);
    font-size: ${scaled(16)};
    font-weight: 400;
    color: var(--color-text-default);
    line-height: 1.2;
    margin: 0;
  }
`;

// Horizontal rail of spotlight cards with hidden scrollbar — cards cut off at
// the right signal more content is reachable via wheel/trackpad/touch.
const ScrollRail = styled.div`
  display: flex;
  gap: var(--space-8);
  overflow-x: auto;
  /* overflow-x: auto hard-clips along BOTH axes at the scroll viewport edges,
     which is what slices the soft card shadow into a hard line. The Figma
     "Tile Shadow" is -2px 10px 30px, so it reaches ~20px above / ~40px below
     / ~32px to each side of a card. Pad the rail so the shadow lands inside
     the clip box. On the left, cancel the pad with an equal negative margin
     so the first card stays flush with the section; the shadow then bleeds
     past the widget boundary instead of clipping there. That's safe with the
     edge-to-edge page layout because only RIGHT overflow widens a page —
     left overhang is never scrollable. For the same reason the right pad has
     no negative margin: it just gives the last card's shadow room at
     end-of-scroll, inside the scroller. Mid-scroll the right edge cuts cards
     anyway, so no bleed is needed there. The top pad is var(--space-8) — it
     doubles as the title→cards spacing (Section has no gap) and still clears
     the shadow's ~20px upward reach. */
  padding: var(--space-8) var(--space-8) var(--space-14);
  margin-left: calc(-1 * var(--space-8));
  scroll-snap-type: x proximity;
  /* Snap aligns cards to the scrollport edge, which the negative margin moved
     space-8 LEFT of the section edge — without this, the initial snap drags
     the rail to scrollLeft = padding-left and the first card paints cut off
     at the widget boundary. Match the snap origin to the padded content edge. */
  scroll-padding-inline: var(--space-8);

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// An anchor, not a button. A button is invisible to a crawler and, more to the
// point, a reader cannot link to it, cmd-click it or copy its address — and a
// spotlight is exactly the view people want to send to someone. The click
// handler takes over for a plain left click; everything else the browser
// already does correctly is left alone (see handleClick below).
const SpotlightCardBase = styled.a<{ active: boolean }>`
  text-decoration: none;
  color: inherit;
  flex: 0 0 auto;
  width: ${scaled(240)};
  /* A floor, not a fixed height: these descriptions run to two or three lines
     and leave the card at 139px, which reads as a strip rather than a tile.
     157px of content plus the 33px vertical padding and the 4px border is
     194px — the height the budget rail's cards reach on their own, so the two
     rails match. A longer description still grows the row past it.
     (e3cca37 dropped the original 180px floor from both widgets; budget keeps
     its shape anyway, because its longest description runs to four lines.) */
  min-height: ${scaled(157)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-5) var(--space-6) var(--space-4);
  background-color: var(--color-bg-surface);
  border-radius: var(--radius-m);
  /* Figma "Tile Shadow" — a soft, large-blur drop shadow offset slightly down
     and left. Inlined rather than using --shadow-m so it matches the template
     exactly. The rail's padding (above) is sized to keep this from clipping. */
  box-shadow: -2px 10px 30px 0 rgba(0, 0, 0, 0.15);
  border: 2px solid ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  scroll-snap-align: start;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: -2px 14px 38px 0 rgba(0, 0, 0, 0.2);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

// h3: a card inside the h2 section above.
const SpotlightTitle = styled.h3`
  && {
    font-family: var(--serif-stack);
    font-size: ${scaled(24)};
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--color-text-default);
    margin: 0;
  }
`;

const SpotlightDescription = styled.p`
  font-family: var(--sans-stack);
  font-size: ${scaled(16)};
  line-height: 1.3;
  color: var(--color-text-default);
  margin: 0;
`;

interface SpotlightCardProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'title'> {
  active: boolean;
}

// aria-current, not aria-pressed: aria-pressed is a button state and is not
// valid on a link.
const SpotlightCard = ({ active, ...rest }: SpotlightCardProps) => (
  <SpotlightCardBase
    {...rest}
    aria-current={active ? 'true' : undefined}
    active={active}
  />
);

interface SpotlightsProps {
  /** Slug of the active spotlight, or null. */
  activeSpotlightId: string | null;
  onSpotlightClick: (spotlight: Spotlight) => void;
  onClearSpotlight: () => void;
}

export function Spotlights({ activeSpotlightId, onSpotlightClick, onClearSpotlight }: SpotlightsProps) {
  const m = useT();
  // Definitions come from GET /api/spotlights (investments.spotlights), so new
  // spotlights appear without a widget deploy. Until loaded (or on error) the
  // rail renders nothing — the section heading without cards would look broken.
  const [spotlights, setSpotlights] = useState<Spotlight[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchSpotlights()
      .then(data => { if (!cancelled) setSpotlights(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Clicking the active card clears it — matches the budget widget's UX.
  //
  // Modified clicks are left to the browser: cmd/ctrl-click, middle-click and
  // shift-click open the filtered view in a new tab or window, which is what a
  // card that looks like a link is expected to do. Only a plain left click is
  // handled in place.
  const handleClick = (e: MouseEvent<HTMLAnchorElement>, spotlight: Spotlight) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (activeSpotlightId === spotlight.slug) {
      onClearSpotlight();
    } else {
      onSpotlightClick(spotlight);
    }
  };

  // The host page's path never changes under us, so read it once per render
  // rather than reaching into window.location inside the map below.
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
  const hostSearch = typeof window === 'undefined' ? '' : window.location.search;

  // App re-renders on every filter change and on every frame of a slider drag,
  // and this component renders with it. Each href costs a URLSearchParams
  // round-trip, so build them only when something they depend on moves.
  const hrefs = useMemo(() => {
    const byslug: Record<string, string> = {};
    for (const s of spotlights) {
      byslug[s.slug] = pathname + toSearchString(
        spotlightQuery(activeSpotlightId === s.slug ? null : s.slug, hostSearch),
      );
    }
    return byslug;
  }, [spotlights, activeSpotlightId, pathname, hostSearch]);

  if (spotlights.length === 0) return null;

  return (
    <Section>
      <SectionTitle>{m.spotlights.title}</SectionTitle>
      <ScrollRail>
        {spotlights.map(spotlight => (
          <SpotlightCard
            key={spotlight.slug}
            active={activeSpotlightId === spotlight.slug}
            href={hrefs[spotlight.slug]}
            onClick={e => handleClick(e, spotlight)}
          >
            <SpotlightTitle>{spotlight.title}</SpotlightTitle>
            {spotlight.description && (
              <SpotlightDescription>{spotlight.description}</SpotlightDescription>
            )}
          </SpotlightCard>
        ))}
      </ScrollRail>
    </Section>
  );
}
